import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  DISPLAY_SUITS,
  SUIT_GLYPHS,
  SUIT_NAMES,
  isRed,
  suitOf,
  type CardId,
  type Seat,
  type Suit,
} from '@ubs/rules';
import type { PublicSeat, ViewMessage } from '@ubs/protocol';
import type { DisplayState } from '../game/display.js';
import {
  emptySlots,
  filledSlots,
  slotsReady,
  slotsToGives,
  toggleSlot,
  type Slots,
} from '../game/selection.js';
import { Card, CardBack, CardChip, FloorCards, type Entry } from './Card.js';
import { Countdown } from './Countdown.js';
import { TimerRing, TIMER_TEXT_MIN_MS } from './TimerRing.js';

/** Sunucu `deadlineTotal` göndermediyse varsayılan. */
const TURN_TOTAL_MS = 15_000;
/** El başı kart sayısı. */
const HAND_MAX = 16;
/** Yerdeki kapalı kart sayısı. */
const KITTY_SIZE = 4;
/** Maç yapısı — kural motorunun varsayılan yapılandırmasıyla aynı. */
const DEALS_PER_SET = 4;
const SETS = 3;
const TOTAL_DEALS = DEALS_PER_SET * SETS;
/** 8'cinin göreceği gömme adedi. */
const BURY_COUNT = 4;

/** Kendi koltuğum dahil üç kenar. Sıra 8 → 5 → 3 yönü SAĞA doğru akıyor. */
type SeatPos = 'bottom' | 'right' | 'left';

/** Öbekteki kartın eğikliği — masaya atılmış kart hissi, sahibinin yönünde. */
const PILE_TILT: Record<SeatPos, number> = { left: -8, right: 8, bottom: 2 };

interface Props {
  view: ViewMessage;
  display: DisplayState;
  seats: PublicSeat[];
  /** Animasyon kuyruğu oynuyor mu — kartların kalkışı bunu bekliyor. */
  busy: boolean;
  /** Bu faz için seçimim gönderildi, karşılık bekleniyor. */
  sent: boolean;
  /** Kartların dağıtılacağı an (epoch ms); geri sayım masanın üstünde döner. */
  startsAt: number | null;
  /**
   * Girdi kapısı: kuyruğu beklemeyen EN SON durum. Hangi kartın atılabileceği
   * ekrandaki animasyona değil gerçek duruma bağlı.
   */
  live: ViewMessage;
  /** "Oyuna başla" düğmesi gösterilsin mi. */
  canStart: boolean;
  onStart(): void;
  /** Tek geçerli kartın kendiliğinden oynanması açık mı. */
  autoPlay: boolean;
  onToggleAutoPlay(): void;
  chat: Partial<Record<Seat, string>>;
  phrases: string[];
  onPlay(card: CardId): void;
  onTrump(suit: Suit): void;
  onBury(cards: CardId[]): void;
  onExchange(gives: { to: Seat; cards: CardId[] }[]): void;
  onChat(text: string): void;
  onQuit(): void;
}

export function Table({
  view,
  display,
  seats,
  busy,
  sent,
  startsAt,
  live,
  canStart,
  onStart,
  autoPlay,
  onToggleAutoPlay,
  chat,
  phrases,
  onPlay,
  onTrump,
  onBury,
  onExchange,
  onChat,
  onQuit,
}: Props) {
  /** Gömme ve alışverişte kullanılan yuvalar; oyunda seçim yok, doğrudan atılıyor. */
  const [slots, setSlots] = useState<Slots>([]);
  const [boardOpen, setBoardOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  /**
   * Çıkış onayı. ✕ doğrudan çıkarıyordu ve düğme elin hemen üstünde, parmağın
   * gezindiği yerde duruyor — yanlışlıkla basılınca maç terk edilmiş sayılıyor.
   * Geri dönüşü olmayan tek hareket bu, o yüzden soruyoruz.
   */
  const [quitOpen, setQuitOpen] = useState(false);

  /**
   * Oyun alanının ölçüsü. Rakip kartının uçuş başlangıcı buradan hesaplanıyor;
   * sabit piksel verilince masa büyüyünce kart oyuncunun yerinden değil
   * ortadan doğuyordu.
   */
  const playRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  /**
   * Atılan KENDİ kartımın çıkış noktası — kartın elde durduğu yer, yığının
   * alt yuvasına göre.
   *
   * Tıklama anında ölçülüyor: kart o an hâlâ elde duruyor ve gerçek konumu
   * biliniyor. Sabit bir başlangıç (ekranın alt ortası) verildiğinde kart
   * önce ortaya kayıp oradan yukarı çıkıyordu.
   */
  const pileRef = useRef<HTMLDivElement>(null);
  const flightFrom = useRef<{ card: CardId; x: number; y: number } | null>(null);
  useEffect(() => {
    const el = playRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isRole8 = view.role8 === view.you;
  /**
   * Sıra bende mi — `live` üzerinden, `busy` bakılmadan.
   *
   * Önce `!busy` de aranıyordu ve son animasyon bitene kadar kart
   * atılamıyordu. Oysa geçerlilik zaten gerçek duruma bakıyor: kuyruk
   * oynarken sıra bana geçtiyse atabilmeliyim, kartım da animasyonun
   * arkasına düşer.
   */
  const myTurn = live.phase === 'play' && live.turn === live.you;
  /**
   * Kartlar kuyruğu BEKLEMEDEN kalkıyor.
   *
   * Bir ara kuyruğun boşalması bekleniyordu (animasyonlar üst üste binmesin
   * diye), ama tur toplanırken sıra bende olduğunda hangi kartı
   * atabileceğimi göremiyordum — oysa oynayabiliyordum. Vurgu ile hak artık
   * aynı anda geliyor.
   *
   * Tek istisna elin başı: `liftReady` (aşağıda).
   */
  /**
   * Oyun fazına girer girmez oynanabilir kartlar kalkmıyor, kısa bir soluk
   * bekliyor.
   *
   * 8'ci ilk turu açtığı için elindeki HER kart oynanabilir: hepsi birden
   * kalkınca alışverişte gelen kartların vurgusu içinde kaybolup gidiyordu.
   * Önce gelenler görünüyor, sonra el oyuna hazırlanıyor.
   */
  const [liftReady, setLiftReady] = useState(false);
  useEffect(() => {
    if (view.phase !== 'play') {
      setLiftReady(false);
      return;
    }
    const id = setTimeout(() => setLiftReady(true), 900);
    return () => clearTimeout(id);
  }, [view.phase, view.dealIndex]);

  const showPlayable = myTurn && liftReady;
  const trumping = view.phase === 'trump' && isRole8 && !sent;
  const burying = view.phase === 'bury' && isRole8 && !sent;
  const exchangeTargets: Seat[] =
    view.exchange?.mustGive?.flatMap((g) => new Array<Seat>(g.count).fill(g.to)) ?? [];
  const exchanging = view.phase === 'exchange' && exchangeTargets.length > 0 && !sent;

  /**
   * Yuvalar YALNIZCA faz ya da el değişince sıfırlanıyor.
   * Her `seq`'te sıfırlamak bir hataydı: karşı taraf hamlesini yaptığında gelen
   * görünüm, oyuncunun yarım kalan seçimini sessizce siliyordu.
   */
  useEffect(() => {
    if (view.phase === 'bury') setSlots(emptySlots(BURY_COUNT));
    else if (view.phase === 'exchange') setSlots(emptySlots(exchangeTargets.length));
    else setSlots([]);
    // `exchangeTargets.length` bilerek bağımlılıkta: kuyruk ilerleyip sıra bana
    // geldiğinde yuva sayısı değişiyor.
  }, [view.phase, view.dealIndex, exchangeTargets.length]);

  const selecting = burying || exchanging;

  /**
   * Alışverişte bana geçen kartlar — ELDE, birkaç saniye yukarıda duruyor.
   *
   * Ayrı bir duyuru kutusu vardı ve kaldırıldı: kutu kartların üstünü
   * kapatıyor, üstelik "hangileri geldi" sorusunu elin içinde aramayı
   * gerektiriyordu. Kartın kendisinin kalkması, atılabilir kartları
   * göstermenin aynısı — öğrenilecek yeni bir işaret yok.
   *
   * Sonra kendiliğinden iniyor: kalıcı kalsa elin dizilişi bozuk görünüyor ve
   * hangi kartın neden yukarıda olduğu zamanla anlamını yitiriyor.
   */
  const incoming = new Set(
    view.myExchange.flatMap((t) => (t.from === view.you ? t.received : t.given)),
  );
  const [liftIncoming, setLiftIncoming] = useState(false);
  const seenTransfers = useRef(0);
  const liftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const count = view.myExchange.length;
    // Yeni el: kayıt sıfırlanıyor, sayaç da onunla.
    if (count < seenTransfers.current) seenTransfers.current = count;
    if (count <= seenTransfers.current) return;

    seenTransfers.current = count;
    if (liftTimer.current) clearTimeout(liftTimer.current);
    setLiftIncoming(true);
    liftTimer.current = setTimeout(() => setLiftIncoming(false), 4000);
    // Bağımlılık dizinin KENDİSİ değil uzunluğu: `myExchange` her görünümde
    // yeni bir dizi olarak geliyor ve etki boşuna yeniden koşuyordu.
  }, [view.myExchange.length]);

  useEffect(
    () => () => {
      if (liftTimer.current) clearTimeout(liftTimer.current);
    },
    [],
  );

  const tap = (card: CardId, element: HTMLElement) => {
    if (selecting) {
      setSlots((current) => toggleSlot(current, card));
      return;
    }
    if (!myTurn || !live.legalCards.includes(card)) return;

    const pile = pileRef.current;
    if (pile) {
      const from = element.getBoundingClientRect();
      const to = pile.getBoundingClientRect();
      // Alt yuva, yığın kutusunun merkezinden `0.12 × kart boyu` aşağıda
      // duruyor (bkz. `.pile__slot--bottom`).
      flightFrom.current = {
        card,
        x: from.left + from.width / 2 - (to.left + to.width / 2),
        y: from.top + from.height / 2 - (to.top + to.height / 2 + from.height * 0.12),
      };
    }
    onPlay(card);
  };

  // Koltukları kendi koltuğum altta olacak şekilde döndür. Sıra saat yönünde
  // SAĞA akıyor: benden sonra oynayan sağdaki.
  const [mySeat, rightSeat, leftSeat] = [0, 1, 2].map((i) => ((view.you + i) % 3) as Seat) as [
    Seat,
    Seat,
    Seat,
  ];
  const posOf = (seat: Seat): SeatPos =>
    seat === mySeat ? 'bottom' : seat === rightSeat ? 'right' : 'left';

  /**
   * Atılan kartın nereden uçacağı — yığının merkezine göre sapma.
   * Ölçüm gelmeden önce (ilk kare) makul bir alt sınır kullanılıyor.
   */
  const entryOf = (seat: Seat, card: CardId): Entry => {
    // Yan koltuklar masanın dikey ortasında: kart YANDAN geliyor, tepeden
    // değil. Dikey bileşen verilirse kart oyuncunun yerinden değil köşeden
    // doğuyor gibi görünüyor.
    const dx = Math.max(150, box.w / 2 - 70);
    if (seat === leftSeat) return { x: -dx, y: 0, rotate: -12 };
    if (seat === rightSeat) return { x: dx, y: 0, rotate: 12 };

    /**
     * Kendi kartım: tıklama anında ölçülen gerçek konumundan uçuyor.
     * Ölçüm yoksa (süre aşımında benim adıma oynandı) elin ortasından.
     */
    const measured = flightFrom.current;
    if (measured && measured.card === card) {
      return { x: measured.x, y: measured.y, rotate: 0 };
    }
    return { x: 0, y: Math.max(90, box.h / 2 - 60), rotate: 0 };
  };

  /**
   * Tur bitince yığın KAZANANIN önüne süpürülüyor.
   *
   * Eli kimin aldığını söylemenin en doğrudan yolu bu: rozet ya da renkli
   * çerçeve yerine kartların gerçekte gittiği yer. Masada da böyle oluyor.
   */
  const sweptTo = display.trickWinner !== null ? posOf(display.trickWinner) : null;
  // Yön yeterli: sönme kartın kendi animasyonunda, ölçek yok.
  const sweep =
    sweptTo === 'left'
      ? { x: -Math.max(150, box.w / 2 - 70), y: 0 }
      : sweptTo === 'right'
        ? { x: Math.max(150, box.w / 2 - 70), y: 0 }
        : sweptTo === 'bottom'
          ? { x: 0, y: Math.max(90, box.h / 2 - 40) }
          : { x: 0, y: 0 };

  const rank = [...view.scores].sort((a, b) => b - a).indexOf(view.scores[view.you] ?? 0) + 1;
  /**
   * Geri sayım CANLI durumdan çiziliyor, gecikmeli görünümden değil.
   *
   * Süre animasyon payını da içerecek şekilde kuruluyor; gecikmeli görünüm
   * ise ancak animasyon bitince uygulanıyor. Halka bu yüzden hep yarısından
   * başlayıp sıfıra iniyor, sonra yine yarıdan başlıyordu. Mutlak bitiş anı
   * zaten `deadline`'da: canlı durumdan okununca halka dolu başlıyor ve
   * kesintisiz iniyor.
   */
  const timerTotal = live.deadlineTotal ?? TURN_TOTAL_MS;
  const nameOf = (seat: Seat) => seats[seat]?.name ?? `Koltuk ${seat}`;
  const seatProps = { view, live, display, seats, chat, total: timerTotal, posOf };

  return (
    <div className="table">
      {/* Masa: ekranın ortasında duran çuha. HER ŞEY bunun içinde — rakipler
          üst köşelerde, atılan kartlar merkezde, kendi elim alt kenarda. */}
      <div className="board">
        {/* Sol üstte istiflenmiş bilgi kutusu: sıralama, el ve set. Aynı
            zamanda puan çizelgesinin düğmesi. */}
        <button
          type="button"
          className="hud"
          onClick={() => setBoardOpen(true)}
          title="Puan çizelgesini gör"
        >
          <span className="hud__row">
            <span className="hud__rank">#{rank}</span>
            <span className="hud__deal">
              <b>
                {view.dealIndex + 1}/{TOTAL_DEALS}
              </b>
              <em>EL</em>
            </span>
          </span>
          <span className="hud__set">
            SET {view.set}/{SETS}
          </span>
        </button>

        {/* Üst sağ araç çubuğu. Üçü tek satırda: sabit piksel uzaklıklarıyla
            yerleştirilince koz göstergesinin genişliği değiştikçe düğmeler
            birbirine giriyordu. */}
        <div className="board__tools">
          {/* İkon "sırayı geç": oynanacak tek kart kaldığında sıranın
              kendiliğinden ilerlemesi. Emoji yerine çizim, çünkü emoji her
              cihazda başka türlü görünüyor ve açık/kapalı halini renkle
              anlatmak emojide mümkün değil. */}
          <button
            className={autoPlay ? 'board__auto board__auto--on' : 'board__auto'}
            onClick={onToggleAutoPlay}
            title={
              autoPlay
                ? 'Otomatik oyna açık — oynanacak tek kart kaldıysa kendiliğinden atılır'
                : 'Otomatik oyna kapalı — kararı sen veriyorsun'
            }
            aria-label="Otomatik oyna"
            aria-pressed={autoPlay}
            type="button"
          >
            <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden>
              <path d="M5 5.5 14 12l-9 6.5v-13Z" fill="currentColor" />
              <rect x="16" y="5.5" width="2.6" height="13" rx="1.3" fill="currentColor" />
            </svg>
          </button>

          {/* Koz göstergesi: elin tamamını belirleyen tek bilgi, hep aynı yerde. */}
          <div className={`trump-badge ${view.trump ? `trump-badge--${view.trump}` : ''}`}>
            {view.trump ? (
              <>
                <b>{SUIT_GLYPHS[view.trump]}</b>
                <em>KOZ</em>
              </>
            ) : (
              <em>KOZ</em>
            )}
          </div>

          <button
            className="board__quit"
            onClick={() => setQuitOpen(true)}
            title="Oyundan çık"
            type="button"
          >
            ✕
          </button>
        </div>

        {/* Yerdeki 4 kapalı kart. Üç oyuncuda üst koltuk boş, oraya oturuyor;
            el boyunca duruyor — önce dağıtılan yer, gömmeden sonra 8'cinin
            gömdükleri. İkisi de kapalı, oyuncu için fark yok: orada 4 kart var. */}
        <FloorCards count={KITTY_SIZE} />

        <div className="board__play" ref={playRef}>
          <PlayerSeat seat={leftSeat} {...seatProps} />
          <PlayerSeat seat={rightSeat} {...seatProps} />
        </div>

        {/* Yığın masanın TAMAMINA göre ortalanıyor, oyun alanına göre değil:
            oyun alanı alt bandın üstünde bittiği için onun merkezi ekranın
            yukarısına düşüyor ve atılan kartlar tepede duruyordu. */}
        <div className="pile-layer">
          {/* Kap DÜZ BİR DIV: hiçbir dönüşümü animasyonlu değil.
              Süpürme burada, kapta yapılıyordu ve kap ölçeklendiği için içeride
              uçmakta olan kartın atası her karede değişiyordu — uçuş bozuluyor,
              kart masaya inerken büyüyor gibi görünüyordu. Süpürme artık
              kartların kendisinde (bkz. `Card` → `sweep`). */}
          <div className="pile" ref={pileRef}>
            {display.pile.map((entry, index) => (
              <div
                key={entry.card}
                className={`pile__slot pile__slot--${posOf(entry.seat)}`}
                /* Eğiklik SABİT, animasyonlu değil — aynı sebeple. */
                style={{
                  zIndex: index,
                  transform: `rotate(${PILE_TILT[posOf(entry.seat)]}deg)`,
                }}
              >
                <Card
                  card={entry.card}
                  from={entryOf(entry.seat, entry.card)}
                  sweep={sweptTo ? sweep : undefined}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Diyaloglar masanın DOĞRUDAN çocuğu, oyun alanının değil: alt bant
            kendi yığın bağlamını kurduğu için oyun alanının içindeyken elin
            ARKASINDA kalıyorlardı. Yuva tıklamayı geçiriyor — gömme ve
            alışverişte kartlar yine elden seçilecek. */}
        {/* Geri sayım MASANIN ÜSTÜNDE dönüyor, ayrı bir bekleme ekranında
            değil: "oyna"ya basınca oyuncu doğrudan masaya oturuyor ve sayım
            oturduğu yerde başlıyor. Sayarken faz panelleri gizli — kartlar
            daha dağıtılmadan koz sorulmamalı. */}
        {startsAt !== null && (
          <div className="countdown-layer">
            <Countdown at={startsAt} />
          </div>
        )}

        {/* Botlara karşı oynarken beklenecek kimse yok: masa hemen açılıyor,
            başlama anını oyuncu seçiyor. */}
        {canStart && (
          <div className="countdown-layer">
            <button type="button" className="btn btn--primary btn--pill btn--big" onClick={onStart}>
              Oyuna başla
            </button>
          </div>
        )}

        <div className="dialog-slot">
          <AnimatePresence mode="wait">
            {startsAt !== null || canStart ? null : display.dealSummary ? (
              <DealSummary
                key="deal"
                summary={display.dealSummary}
                dealIndex={view.dealIndex}
                nameOf={nameOf}
                you={view.you}
                targets={view.targets}
              />
            ) : display.trumpBanner ? (
              <TrumpBanner
                key="banner"
                suit={display.trumpBanner.suit}
                who={nameOf(display.trumpBanner.seat)}
              />
            ) : view.phase === 'exchange' ? (
              <ExchangePanel
                key="exchange"
                view={view}
                sent={sent}
                nameOf={nameOf}
                slots={slots}
                targets={exchangeTargets}
                onSubmit={() => onExchange(slotsToGives(slots, exchangeTargets))}
              />
            ) : view.phase === 'trump' ? (
              <TrumpPanel
                key="trump"
                view={view}
                mine={trumping}
                nameOf={nameOf}
                onPick={onTrump}
              />
            ) : view.phase === 'bury' ? (
              <BuryPanel
                key="bury"
                view={view}
                mine={burying}
                nameOf={nameOf}
                slots={slots}
                onSubmit={() => onBury(filledSlots(slots))}
              />
            ) : null}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {boardOpen && (
            <Scoreboard view={view} seats={seats} onClose={() => setBoardOpen(false)} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {quitOpen && <QuitConfirm onCancel={() => setQuitOpen(false)} onConfirm={onQuit} />}
        </AnimatePresence>

        <div className="board__bottom">
          <div className="board__self">
            <PlayerSeat
              seat={mySeat}
              {...seatProps}
              showCards={false}
              onAvatar={() => setChatOpen((open) => !open)}
            />

            <AnimatePresence>
              {chatOpen && (
                <ChatPicker
                  phrases={phrases}
                  onPick={(text) => {
                    setChatOpen(false);
                    onChat(text);
                  }}
                  onClose={() => setChatOpen(false)}
                />
              )}
            </AnimatePresence>

            {/* Onay düğmesi YALNIZCA ortadaki panelde. Burada da bir kopyası
                vardı ve alt bantta boşken bile yer tutuyordu — o yükseklik
                doğrudan kart boyundan çalınıyordu. */}
          </div>

          <div className="hand-wrap">
            {/* Kutunun kendi genişliği kart eksildikçe değişiyor; `layout`
                onu da yumuşatıyor. Kartların yer değiştirmesi ise kendi
                `layout="position"` ayarlarından geliyor. */}
            <motion.div className="hand" layout>
              {display.hand.map((card, index) => (
                <Card
                  key={card}
                  card={card}
                  // El renge göre bloklu; renk değişen her kart yeni bloğu
                  // açıyor ve soluna ayırıcı boşluk giriyor.
                  groupStart={
                    index > 0 && suitOf(card) !== suitOf(display.hand[index - 1]!)
                  }
                  selected={selecting ? slots.includes(card) : false}
                  playable={
                    showPlayable && !selecting
                      ? live.legalCards.includes(card)
                      : /* Gelen kartların vurgusu oyun başlayınca bitiyor:
                           ikisi üst üste binince ayırt edilemiyorlardı. */
                        liftIncoming && view.phase !== 'play' && incoming.has(card)
                        ? true
                        : undefined
                  }
                  onClick={
                    selecting || (myTurn && live.legalCards.includes(card))
                      ? (element) => tap(card, element)
                      : undefined
                  }
                />
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Koltuk plakası
 * ------------------------------------------------------------------ */

function PlayerSeat({
  seat,
  view,
  live,
  display,
  seats,
  chat,
  total,
  posOf,
  showCards = true,
  onAvatar,
}: {
  seat: Seat;
  /** Ekranda görünen durum: hedef ve puan buradan (el sonu özetiyle uyumlu). */
  view: ViewMessage;
  /** Canlı durum: kimin beklendiği ve geri sayım buradan. */
  live: ViewMessage;
  display: DisplayState;
  seats: PublicSeat[];
  chat: Partial<Record<Seat, string>>;
  total: number;
  posOf: (seat: Seat) => SeatPos;
  showCards?: boolean;
  onAvatar?: (() => void) | undefined;
}) {
  const info = seats[seat];
  const name = info?.name ?? `Koltuk ${seat}`;
  const target = view.targets[seat] ?? 0;
  const taken = display.tricks[seat] ?? 0;
  const count = display.counts[seat] ?? 0;
  /**
   * Sırası gelen koltuk. Oyun dışındaki fazlarda da "kim bekleniyor" belli
   * olmalı: koz ve gömmede 8'ci, alışverişte kuyruğun başındaki.
   */
  const waiting =
    live.phase === 'matchOver'
      ? false
      : live.phase === 'play'
        ? live.turn === seat
        : live.phase === 'exchange'
          ? live.exchange?.turn === seat
          : live.role8 === seat;
  const ringing = waiting && live.deadline !== null && total > 0;
  const counting = ringing && total >= TIMER_TEXT_MIN_MS;
  const chatText = chat[seat];
  const won = display.trickWinner === seat;

  return (
    <div className={`seat seat--${posOf(seat)} ${waiting ? 'seat--active' : ''}`}>
      <div className="seat__plate">
        {/* Jeton = alınan el / hedef. Bu elin tek anlamlı ölçüsü bu.
            Renk hepsinde aynı: rol zaten hedefin kendisinde yazıyor, ayrıca
            renkle kodlamak üç plakayı üç ayrı renge bölüp masayı dağıtıyordu. */}
        <span className={`seat__tally ${taken >= target ? 'is-done' : ''}`}>
          {taken}
          <i>/{target}</i>
        </span>

        <span className="seat__avatar-slot">
          {onAvatar ? (
            <button
              type="button"
              className={`seat__avatar seat__avatar--tap ${counting ? 'is-counting' : ''}`}
              onClick={onAvatar}
              title="Hazır mesaj gönder"
            >
              {!counting && name.slice(0, 1).toUpperCase()}
              {ringing && <TimerRing deadline={live.deadline} total={total} />}
              <span className="seat__avatar-hint" aria-hidden>
                💬
              </span>
            </button>
          ) : (
            <span className={`seat__avatar ${counting ? 'is-counting' : ''}`}>
              {!counting && name.slice(0, 1).toUpperCase()}
              {ringing && <TimerRing deadline={live.deadline} total={total} />}
            </span>
          )}

          {/* Konumlama AYRI bir kutuda, animasyon içeride: Motion `transform`'u
              kendi yazıyor ve CSS'teki ortalamayı eziyor. */}
          <span className="bubble-slot">
            <AnimatePresence>
              {chatText !== undefined && (
                <motion.div
                  key={`${chatText}-${seat}`}
                  className="bubble"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 28 }}
                >
                  {chatText}
                </motion.div>
              )}
            </AnimatePresence>
          </span>
        </span>

        <span className="seat__info">
          <span className="seat__name">
            <span className="seat__name-text">{name}</span>
            {info?.bot && <span className="tag tag--bot">bot</span>}
            {info && !info.connected && <span className="tag tag--off">yok</span>}
          </span>
          {/* Kümülatif puan küçük punto: maçın geneli, elin kendisi değil. */}
          <span className={`seat__score ${scoreClass(view.scores[seat] ?? 0)}`}>
            {formatScore(view.scores[seat] ?? 0)}
          </span>
        </span>

        <AnimatePresence>
          {won && (
            <motion.div
              className="badge badge--trick"
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              EL
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showCards && (
        <div className="seat__cards" aria-label={`${count} kart`}>
          {Array.from({ length: Math.min(count, HAND_MAX) }, (_, i) => (
            <CardBack key={i} side />
          ))}
        </div>
      )}
    </div>
  );
}

const formatScore = (n: number): string => (n > 0 ? `+${n}` : String(n));

/**
 * Ada ayrılma eki takar: "Tansel'den", "Mert'ten", "Ayşa'dan".
 *
 * Sabit "-den" kullanmak "Ayşa-den" gibi bozuk okunuşlar üretiyordu. Kural
 * iki adımda: son ünlü kalınsa `a`, inceyse `e`; son harf sert sessizse `t`,
 * değilse `d`.
 */
function ablative(name: string): string {
  const lower = name.toLocaleLowerCase('tr');
  const vowels = [...lower].filter((c) => 'aeıioöuü'.includes(c));
  const last = vowels[vowels.length - 1] ?? 'e';
  const back = 'aıou'.includes(last);
  const hard = 'çfhkpsşt'.includes(lower[lower.length - 1] ?? '');
  return `${name}'${hard ? 't' : 'd'}${back ? 'a' : 'e'}n`;
}
const scoreClass = (n: number): string => (n > 0 ? 'is-up' : n < 0 ? 'is-down' : '');

/* ------------------------------------------------------------------ *
 * Faz panelleri
 * ------------------------------------------------------------------ */

function Panel({
  title,
  sub,
  children,
  wide,
}: {
  title: string;
  /** Yalnızca "kim bekleniyor" bilgisi. Kural anlatan alt yazı yok. */
  sub?: string | undefined;
  children?: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <motion.section
      className={`phase ${wide ? 'phase--wide' : ''}`}
      initial={{ opacity: 0, y: 14, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
    >
      <header className="phase__head">
        <h3>{title}</h3>
        {sub && <span className="phase__sub">{sub}</span>}
      </header>
      {children}
    </motion.section>
  );
}

/** Alışverişte ne alıp ne verdiğim — koz ve gömme boyunca görünür kalıyor. */
function ExchangeRecap({ view, nameOf }: { view: ViewMessage; nameOf: (s: Seat) => string }) {
  if (view.myExchange.length === 0) return null;
  return (
    <div className="recap">
      {view.myExchange.map((t) => {
        const mine = t.from === view.you;
        const other = mine ? t.to : t.from;
        return (
          <p key={`${t.from}-${t.to}`} className="recap__row">
            <em>{nameOf(other)}</em>
            <span className="recap__side">
              <span className="recap__label">aldın</span>
              {(mine ? t.received : t.given).map((c) => (
                <CardChip key={c} card={c} />
              ))}
            </span>
            <span className="recap__side">
              <span className="recap__label">verdin</span>
              {(mine ? t.given : t.received).map((c) => (
                <CardChip key={c} card={c} />
              ))}
            </span>
          </p>
        );
      })}
    </div>
  );
}

function ExchangePanel({
  view,
  sent,
  nameOf,
  slots,
  targets,
  onSubmit,
}: {
  view: ViewMessage;
  sent: boolean;
  nameOf: (s: Seat) => string;
  slots: Slots;
  targets: readonly Seat[];
  onSubmit(): void;
}) {
  const exchange = view.exchange;
  if (!exchange) return null;
  const mine = exchange.mustGive;

  return (
    /* Başlık AŞAMANIN KENDİSİ. Sıradaki alıcı ortada yazılı olduğu için akış
       adım adım okunuyor — önce biri, o bitince varsa diğeri, sonra oyun.
       Alan kişi kimden kaç kart aldığını da görüyor; diğerleri yalnızca kimin
       aldığını. */
    <Panel
      title={
        mine
          ? 'KART ALIYORSUN'
          : `${nameOf(exchange.turn).toLocaleUpperCase('tr')} KART ALIYOR`
      }
      sub={
        mine
          ? mine.map((g) => `${ablative(nameOf(g.to))} ${g.count} kart`).join(' · ')
          : undefined
      }
    >
      <ExchangeRecap view={view} nameOf={nameOf} />

      {/* Plan herkese açık: kim kimden kaç kart alacak. Kartların kendisi
          yalnızca iki tarafın arasında kalıyor. */}
      <ul className="plan">
        {exchange.plan.map((p, i) => (
          <li key={i} className={p.from === view.you || p.to === view.you ? 'is-me' : ''}>
            <b>{nameOf(p.from)}</b> ← {p.count} kart ← <b>{nameOf(p.to)}</b>
          </li>
        ))}
      </ul>

      {mine && !sent && (
        <div className="slots">
          {slots.map((card, index) => (
            <span key={index} className="slots__item">
              <span className="slots__to">{nameOf(targets[index] ?? 0)}</span>
              {card !== null ? (
                <CardChip card={card} big />
              ) : (
                <span className="chip-card chip-card--empty chip-card--big" />
              )}
            </span>
          ))}
          <button
            type="button"
            className="btn btn--primary btn--pill"
            disabled={!slotsReady(slots)}
            onClick={onSubmit}
          >
            Ver
          </button>
        </div>
      )}

      {mine && sent && <p className="phase__wait">Kartların gitti…</p>}
    </Panel>
  );
}

function TrumpPanel({
  view,
  mine,
  nameOf,
  onPick,
}: {
  view: ViewMessage;
  mine: boolean;
  nameOf: (s: Seat) => string;
  onPick(suit: Suit): void;
}) {
  return (
    <Panel title="KOZ" sub={mine ? undefined : `${nameOf(view.role8)} kozu söylüyor…`}>
      <ExchangeRecap view={view} nameOf={nameOf} />
      {mine && (
        <div className="suits">
          {/* Sıra ELDEKİ DİZİLİMLE aynı (karo, sinek, kupa, maça): koz
              seçerken göz elin üstünde geziyor, iki yerde farklı sıra
              olunca her seferinde yeniden aranıyordu. */}
          {DISPLAY_SUITS.map((suit) => (
            <button
              key={suit}
              type="button"
              className={`suit-btn ${isRed(suit) ? 'suit-btn--red' : 'suit-btn--black'}`}
              onClick={() => onPick(suit)}
            >
              <b>{SUIT_GLYPHS[suit]}</b>
              <em>{SUIT_NAMES[suit]}</em>
            </button>
          ))}
        </div>
      )}
    </Panel>
  );
}

function BuryPanel({
  view,
  mine,
  nameOf,
  slots,
  onSubmit,
}: {
  view: ViewMessage;
  mine: boolean;
  nameOf: (s: Seat) => string;
  slots: Slots;
  onSubmit(): void;
}) {
  return (
    <Panel title="GÖMME" sub={mine ? undefined : `${nameOf(view.role8)} gömüyor…`}>
      <ExchangeRecap view={view} nameOf={nameOf} />
      {mine && (
        <div className="slots">
          {slots.map((card, index) =>
            card !== null ? (
              <CardChip key={card} card={card} big />
            ) : (
              <span key={`slot-${index}`} className="chip-card chip-card--empty chip-card--big" />
            ),
          )}
          <button
            type="button"
            className="btn btn--primary btn--pill"
            disabled={!slotsReady(slots)}
            onClick={onSubmit}
          >
            Göm
          </button>
        </div>
      )}
      {/* 8'ci gömdükten sonra aldığı kartları kendi elinde görüyor; ayrıca
          göstermeye gerek yok — diğerleri ise asla göremiyor. */}
    </Panel>
  );
}

function TrumpBanner({ suit, who }: { suit: Suit; who: string }) {
  return (
    <motion.div
      className={`trump-flash trump-flash--${isRed(suit) ? 'red' : 'black'}`}
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.15 }}
      transition={{ type: 'spring', stiffness: 300, damping: 22 }}
    >
      <b>{SUIT_GLYPHS[suit]}</b>
      <span>
        {who} <em>{SUIT_NAMES[suit].toLowerCase()} dedi</em>
      </span>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ *
 * El sonu ve çizelge
 * ------------------------------------------------------------------ */

function DealSummary({
  summary,
  dealIndex,
  nameOf,
  you,
  targets,
}: {
  summary: NonNullable<DisplayState['dealSummary']>;
  dealIndex: number;
  nameOf: (seat: Seat) => string;
  you: Seat;
  targets: readonly number[];
}) {
  const rows = ([0, 1, 2] as Seat[])
    .map((seat) => ({ seat, delta: summary.deltas[seat] ?? 0 }))
    .sort((a, b) => b.delta - a.delta);

  return (
    <motion.section
      className="phase phase--wide"
      initial={{ opacity: 0, y: 18, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
    >
      <header className="phase__head">
        <h3>EL BİTTİ</h3>
        <span className="phase__sub">
          El {dealIndex}/{TOTAL_DEALS} · koz {SUIT_GLYPHS[summary.trump]}
        </span>
      </header>

      <table className="board-table">
        <tbody>
          {rows.map(({ seat, delta }) => (
            <tr key={seat} className={seat === you ? 'is-me' : undefined}>
              <td className="board-table__name">{nameOf(seat)}</td>
              <td className="board-table__tally">
                {summary.tricks[seat]}/{targets[seat] ?? 0}
              </td>
              <td className={`board-table__gain ${delta > 0 ? 'is-up' : delta < 0 ? 'is-down' : ''}`}>
                {formatScore(delta)}
              </td>
              <td className="board-table__score">{formatScore(summary.scores[seat] ?? 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </motion.section>
  );
}

function Scoreboard({
  view,
  seats,
  onClose,
}: {
  view: ViewMessage;
  seats: PublicSeat[];
  onClose(): void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows = [...seats].sort((a, b) => (view.scores[b.seat] ?? 0) - (view.scores[a.seat] ?? 0));

  return (
    <motion.div
      className="scrim"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.section
        className="dialog dialog--wide"
        initial={{ y: 16, scale: 0.96 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 8, scale: 0.97 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h3>PUAN ÇİZELGESİ</h3>
          <span className="dialog__sub">
            El {view.dealIndex + 1}/{TOTAL_DEALS} · Set {view.set}/{SETS}
          </span>
        </header>

        {/* Yatay kaydırma: 12 el dar ekrana sığmıyor. İsim ve toplam sütunları
            yapışık kalıyor — kaydırırken kimin satırına baktığını kaybetmiyorsun. */}
        <div className="sheet">
          <table className="board-table board-table--sheet">
            <thead>
              <tr>
                <th className="sheet__name" rowSpan={2}>
                  Oyuncu
                </th>
                {Array.from({ length: SETS }, (_, s) => (
                  <th key={s} className="sheet__set" colSpan={DEALS_PER_SET}>
                    SET {s + 1}
                  </th>
                ))}
                <th className="sheet__total" rowSpan={2}>
                  Puan
                </th>
              </tr>
              <tr>
                {Array.from({ length: TOTAL_DEALS }, (_, d) => {
                  const result = view.dealResults[d];
                  return (
                    <th
                      key={d}
                      className={`sheet__deal ${d === view.dealIndex ? 'is-now' : ''} ${
                        d % DEALS_PER_SET === 0 ? 'is-set-start' : ''
                      }`}
                    >
                      {result ? SUIT_GLYPHS[result.trump] : d + 1}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((info, index) => (
                <tr key={info.seat} className={info.seat === view.you ? 'is-me' : undefined}>
                  <td className="sheet__name">
                    <span className={`place place--${index + 1}`}>{index + 1}</span>
                    <span className="board-table__name">
                      {info.name}
                      {info.bot && <span className="tag tag--bot">bot</span>}
                    </span>
                  </td>
                  {Array.from({ length: TOTAL_DEALS }, (_, d) => {
                    const result = view.dealResults[d];
                    const delta = result?.deltas[info.seat];
                    return (
                      <td
                        key={d}
                        className={`sheet__cell ${
                          delta === undefined ? '' : delta > 0 ? 'is-up' : delta < 0 ? 'is-down' : ''
                        } ${d % DEALS_PER_SET === 0 ? 'is-set-start' : ''}`}
                      >
                        {delta === undefined ? '·' : formatScore(delta)}
                      </td>
                    );
                  })}
                  <td className="sheet__total">{formatScore(view.scores[info.seat] ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="button" className="btn btn--primary btn--pill" onClick={onClose}>
          Kapat
        </button>
      </motion.section>
    </motion.div>
  );
}

/**
 * Çıkış onayı. Sonucu söylüyor, "emin misin?" demekle yetinmiyor. Varsayılan
 * eylem VAZGEÇ; Esc, dışarı dokunma ve ilk düğme aynı güvenli yere çıkıyor.
 */
function QuitConfirm({ onCancel, onConfirm }: { onCancel(): void; onConfirm(): void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <motion.div
      className="scrim"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.section
        className="dialog"
        initial={{ y: 16, scale: 0.96 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 8, scale: 0.97 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h3>OYUNDAN ÇIK</h3>
          <span className="dialog__sub">Maç yarıda kalır.</span>
        </header>
        <div className="actions">
          <button className="btn btn--pill" onClick={onCancel} type="button" autoFocus>
            Vazgeç
          </button>
          <button className="btn btn--pill btn--danger" onClick={onConfirm} type="button">
            Çık
          </button>
        </div>
      </motion.section>
    </motion.div>
  );
}

/**
 * Hazır mesaj listesi — avatarın hemen üstünde açılan kaydırmalı balon.
 * Diyalog denendi ve bırakıldı: oyunu kilitliyordu. Mesaj göndermek sıra
 * beklemeyi gerektirmez.
 */
function ChatPicker({
  phrases,
  onPick,
  onClose,
}: {
  phrases: string[];
  onPick(text: string): void;
  onClose(): void;
}) {
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (box.current?.contains(target)) return;
      // Avatar düğmesi listeyi zaten açıp kapatıyor; burada karışmasın.
      if ((target as Element).closest?.('.seat__avatar--tap')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown);
    };
  }, [onClose]);

  return (
    /* Konumlama AYRI bir kutuda, animasyon içeride: Motion `transform`'u
       kendi yazıyor ve CSS'teki ortalamayı (`translateX(-50%)`) eziyor —
       liste bu yüzden masanın soluna yapışıyordu. */
    <span className="chat-slot">
    <motion.div
      ref={box}
      className="chat-pop"
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 460, damping: 32 }}
    >
      {phrases.map((text) => (
        <button key={text} type="button" className="chat-pop__item" onClick={() => onPick(text)}>
          {text}
        </button>
      ))}
    </motion.div>
    </span>
  );
}
